import { uniqueId } from 'lodash';
import React from 'react';

import { Icon } from '@opendoor/bricks';
import { trackEvent } from '../../api/analytics';
import OnePdfUpload from '../../components/common/OnePdfUpload';
import PdfSplittingModal, {
  ISplitDocumentMetadata,
  ISplitPdf,
} from '../../components/common/PdfSplittingModal';
import FileUpload from '../common/FileUpload/FileUpload';
import AddFileUploadElement from './AddFileUploadElement';

import './DocumentUploadCardStyle.less';

export interface IDocumentObject {
  displayName?: string;
  documentType: string | null;
  endPage?: number;
  file: File;
  fileName: string | null;
  s3Key?: string;
  required: boolean;
  startPage?: number;
  uniqueId: string;
}

interface IDocumentUploadCardProps {
  documents: IDocumentObject[];
  documentsWithUploadErrors: Set<string | number>;
  listingPacketUrl?: string;
  listingSlug?: string;
  onAddFileUploadElement: (newFile: IDocumentObject) => void;
  onClosePdfSplittingModal: () => void;
  onDeleteFile: (uniqueId: string) => void;
  onDeletePdf?: (uniqueId: string) => void;
  onEditPages?: (file: File) => void;
  onDropAccepted: (file: File, uniqueId: string) => void;
  onSubmitDocumentToSplit: (splitPdf: ISplitPdf) => void;
  pdfSplittings: {
    [id: string]: ISplitPdf;
  };
  updateDocuments?: () => void;
  workingPdf: ISplitPdf | null;
  hasBeenSubmitted: boolean;
}

interface IDocumentUploadCardState {
  pdfFileToBeSplit: File | null;
}

export default class DocumentUploadCard extends React.PureComponent<
  IDocumentUploadCardProps,
  IDocumentUploadCardState
> {
  public state: IDocumentUploadCardState = { pdfFileToBeSplit: null };

  public defaultDocumentName = 'Miscellaneous Document';

  public findDocumentInPdfSplitting = (uniqueId: string): ISplitDocumentMetadata => {
    const { pdfSplittings } = this.props;
    if (!pdfSplittings) {
      return;
    }
    for (const key of Object.keys(pdfSplittings)) {
      const matchingDocument = pdfSplittings[key].documentSplittings.find(document => {
        return document.uniqueId === uniqueId;
      });
      if (matchingDocument) {
        return matchingDocument;
      }
    }
  };

  public handleSubmitDocumentToSplit = (splitPdf: ISplitPdf) => {
    const { onSubmitDocumentToSplit } = this.props;
    this.setState({ pdfFileToBeSplit: null });
    onSubmitDocumentToSplit(splitPdf);
  };

  public handleDropAcceptedPdf = (file: File) => {
    this.setState({ pdfFileToBeSplit: file });
  };

  public handleClosePdfSplittingModal = () => {
    const { onClosePdfSplittingModal } = this.props;
    this.setState({ pdfFileToBeSplit: null });
    onClosePdfSplittingModal();
  };

  public render(): JSX.Element {
    const {
      documents,
      documentsWithUploadErrors,
      listingPacketUrl,
      pdfSplittings,
      onDeleteFile,
      onDeletePdf,
      onDropAccepted,
      onEditPages,
      workingPdf,
      hasBeenSubmitted,
    } = this.props;

    const { pdfFileToBeSplit: pdfFileToBeSplitState } = this.state;

    const pdfFileToBeSplit = pdfFileToBeSplitState || (workingPdf && workingPdf.pdfFile) || null;
    const pdfSplittingModalDocuments = (workingPdf && workingPdf.documentSplittings) || [];

    return (
      <div className="document-upload-card od-offer-detail-form od-base-box box no-padding">
        <div className="top-section less-padding">
          <section className="row header">
            <div className="col-xs-1">
              <Icon className="right-arrow" name="arrow-right-square" />
            </div>
            <div className="col-xs-11">
              <h6>Step 2 of 3: Upload your offer documents*</h6>
            </div>
          </section>

          <section className="row body-section">
            <div className="col-xs-offset-1 col-xs-10">
              <div className="pdf-splitting-section">
                <div className="single-pdf-header">
                  <div>
                    <strong>Have a single PDF containing multiple documents?</strong>
                  </div>
                  Upload it here and let us know what's inside.
                </div>
                {Object.keys(pdfSplittings).map(pdfId => (
                  <FileUpload
                    displayName="Multiple Files"
                    file={pdfSplittings[pdfId].pdfFile}
                    hasSplitPdf={true}
                    key={pdfId}
                    onDropAccepted={onDropAccepted}
                    onDeleteFile={onDeletePdf}
                    onEditPages={onEditPages}
                    uniqueId={pdfId}
                    uploadFailed={documentsWithUploadErrors.has(pdfId)}
                  />
                ))}
                <OnePdfUpload
                  onClickUploadButton={this.trackClicksOnUploadPdf}
                  onDropAccepted={this.handleDropAcceptedPdf}
                />
                <PdfSplittingModal
                  isOpen={!!pdfFileToBeSplit || !!workingPdf}
                  onCloseModal={this.handleClosePdfSplittingModal}
                  onOpenModal={this.trackOpen1PDF}
                  pdfFile={pdfFileToBeSplit}
                  pdfSplittingModalDocuments={pdfSplittingModalDocuments}
                  requiredDocuments={
                    !!workingPdf
                      ? pdfSplittingModalDocuments
                      : documents
                          .filter(document => {
                            return (
                              document.displayName !== this.defaultDocumentName && !document.file
                            );
                          })
                          .map(document => ({
                            displayName: document.displayName,
                            isNameInvalid: false,
                            isPageDataInvalid: false,
                            uniqueId: document.uniqueId,
                          }))
                  }
                  submitDocumentToSplit={this.handleSubmitDocumentToSplit}
                />
              </div>
              <div className="file-upload-section">
                <p className="file-upload-header">
                  <span>Or, you can upload individual PDF files below.</span>
                  {listingPacketUrl && (
                    <a className="required-docs-link" target="_blank" href={listingPacketUrl}>
                      Download required documents
                    </a>
                  )}
                </p>
                {documents.map(component => {
                  const documentMetadata = this.findDocumentInPdfSplitting(component.uniqueId);
                  return (
                    <FileUpload
                      {...component}
                      documentMetadata={documentMetadata}
                      key={component.uniqueId}
                      onDropAccepted={onDropAccepted}
                      onDeleteFile={onDeleteFile}
                      onEditPages={onEditPages}
                      uploadFailed={documentsWithUploadErrors.has(component.uniqueId)}
                      assertNotNull={hasBeenSubmitted && component.required}
                    />
                  );
                })}
                <AddFileUploadElement
                  content="Upload additional documents"
                  onAddFileUploadElement={this.addFileUploadElement}
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  private addFileUploadElement = () => {
    const newDoc = {
      displayName: this.defaultDocumentName,
      documentType: null,
      file: null,
      fileName: null,
      required: false,
      uniqueId: uniqueId('manual_upload_'),
    };

    this.props.onAddFileUploadElement(newDoc);
  };

  private trackClicksOnUploadPdf = () => {
    const { listingSlug } = this.props;
    trackEvent('resale-offer-portal', 'click', 'upload-pdf', {
      listing_slug: listingSlug,
    });
  };

  private trackOpen1PDF = () => {
    const { listingSlug } = this.props;
    trackEvent('resale-offer-portal', 'open', 'pdf-splitting-modal', {
      listing_slug: listingSlug,
    });
  };
}
